import React from "react";
import { FormattedMessage } from "react-intl";

import { API_PATIENT_IMAGE } from "~/config";
import type { TCorrectionMediaView, TPatient } from "~/types/patient";

import PatientCorrectionPhoto from "../patient_correction_photo";

/** Reserve two label lines so photo rows stay aligned when a title wraps. */
const photoLabelClass = "tw-mb-1.5 tw-block tw-min-h-10";

export function PatientCardPhotos({ patient }: { patient: TPatient }) {
  const { required_images } = patient.media;
  const prefix = API_PATIENT_IMAGE(patient.patient_id, "");

  return (
    <div>
      <h3 className="tw-mb-5 tw-mt-6 tw-border-t tw-border-[#E5E5E5] tw-pt-3 tw-text-2xl tw-font-normal tw-leading-[1.2] tw-text-foreground" id="photo-protocol-block">
        <FormattedMessage id="PHOTO_BEFORE" />
      </h3>

      <div className="row">
        <div className="col-md-4" id="photo-profile">
          <div className="form-group">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_PROFILE" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.profile} />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group" id="photo-front-smile">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_SMILE" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.full_face_with_smile} />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group" id="photo-front-no-smile">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_PORTRATE" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.full_face_without_smile} />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="form-group" id="photo-upper-occlusal">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_UPPER_OCCLUSAL" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.occlusal_view_upper} />
          </div>
        </div>

        <div className="col-md-4" />

        <div className="col-md-4">
          <div className="form-group" id="photo-lower-occlusal">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_LOWER_OCCLUSAL" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.occlusal_view_lower} />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="form-group" id="photo-lateral-right">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_RIGHT" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.lateral_view_right} />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group" id="photo-intraoral-front">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_FRONT" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.front_view} />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group" id="photo-lateral-left">
            <span className={photoLabelClass}>
              <FormattedMessage id="PHOTO_LEFT" />
            </span>

            <RequiredPhoto prefix={prefix} view={required_images.lateral_view_left} />
          </div>
        </div>
      </div>

      {Object.keys(patient.media.correction_media).length == 0 ? null : <PatientCorrectionPhoto patient={patient} />}
    </div>
  );
}

function RequiredPhoto({ prefix, view }: { prefix: string; view: TCorrectionMediaView | undefined }) {
  if (view) {
    // Open in the same tab. A new tab starts a fresh SPA instance for this raw-image navigation and can
    // lose the authenticated application state. This is a same-origin full-page navigation, not an app route.
    // tab boots a brand-new SPA instance from scratch, and a mouse/trackpad back-gesture in that
    // tab (unlike the on-screen Back button, which has nothing to go back to) can re-navigate it
    // to the patient page, cold-booting the SPA there and racing `initializeUser()` before render
    // — if it loses that race the doctor sees `/login`, even though the ORIGINAL tab's session was
    // never touched. Same-tab navigation reuses the already-authenticated tab, so this can't happen.
    return (
      <a href={prefix + view.linkto}>
        {/* Use `linkto` (~1280px), not `thumbnail` (~200px). This card renders at ~300–360px, so the thumbnail is
            upscaled ~1.5-1.8x and looked visibly soft, even though the full photo on disk was sharp. */}
        <img src={prefix + view.linkto + `?${Number(new Date())}`} width="100%" alt="" />
      </a>
    );
  }

  return null;
}
